import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { FullAgencyDto, OrganizationSummaryDto } from "@intelligems/ig-types";
import { Box, Drawer, Grid } from "@mui/material";
import { useCreateOrganizationAgencyLink } from "@src/api/organization-agency-links/create-organization-agency-link";
import { useGetOrganizationAgencyLinksByAgencyId } from "@src/api/organization-agency-links/get-organization-agency-links-by-agency-id";
import { useGetOrganizationSummaries } from "@src/api/organization-summaries/get-organization-summaries";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import { ChooseAdminOrgField } from "@src/features/admin/components/Fields/ChooseAdminOrgField";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";

interface ContentProps {
  agency: FullAgencyDto;
}

const Content = ({ agency }: ContentProps) => {
  const adminOrgs = useGetOrganizationSummaries();

  const [org, setOrg] = useImmer<OrganizationSummaryDto | null>(null);

  const dispatch = useDispatch();
  const organizationAgencyLinks = useGetOrganizationAgencyLinksByAgencyId(
    agency.id
  );

  const createOrganizationAgencyLink = useCreateOrganizationAgencyLink();

  if (!organizationAgencyLinks.isSuccess || !adminOrgs.isSuccess) {
    return (
      <Box sx={{ borderTop: 1, borderColor: "divider" }}>
        <Grid container direction={"column"} rowSpacing={2} mt={2}>
          <SkeletonGroup lines={4} />
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ borderTop: 1, borderColor: "divider" }}>
      <Grid container direction={"column"} rowSpacing={2} mt={2}>
        <Grid item>
          <ChooseAdminOrgField
            disabled={createOrganizationAgencyLink.isPending}
            org={org}
            onChange={(value) => {
              setOrg(value);
            }}
          />
        </Grid>
        <Grid item textAlign="end">
          <IgButton
            loading={createOrganizationAgencyLink.isPending}
            variant={"contained"}
            disabled={!org}
            onClick={() => {
              if (org) {
                createOrganizationAgencyLink.mutate(
                  {
                    body: {
                      organizationAgencyLink: {
                        orgId: org.id,
                        agencyId: agency.id,
                        shopId: org.shopId,
                        stripeSubscriptionIds: agency.subscriptions.map(
                          (sub) => ({
                            stripeSubscriptionId: sub.stripeSubscriptionId,
                            version: sub.version,
                          })
                        ),
                      },
                    },
                    pathParams: { agencyId: agency.id },
                    queryParams: undefined,
                  },
                  {
                    onSuccess: () => {
                      dispatch(closeDrawer("addOrganizationAgencyLink"));
                    },
                  }
                );
              }
            }}
          >
            Link
          </IgButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export const AddOrganizationAgencyLinkDrawer = () => {
  const drawer = useSelector(getDrawer("addOrganizationAgencyLink"));
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeDrawer("addOrganizationAgencyLink"));
  };

  if (!drawer?.agency) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open
      onClose={() => handleClose()}
      sx={{
        zIndex: 100,
        ".MuiDrawer-paper": { borderRadius: "0 !important" },
      }}
    >
      <Grid
        direction={"column"}
        container
        sx={{ width: 800, p: 3 }}
        rowSpacing={3}
      >
        <Grid item sx={{ marginBottom: "20px !important" }}>
          <IgTypography variant="h3">Add Organization to Agency</IgTypography>
        </Grid>
        <Content agency={drawer.agency} />
      </Grid>
    </Drawer>
  );
};
